import {inject, Injectable} from '@angular/core';
import {HttpClient, HttpParams} from '@angular/common/http';
import {Observable} from 'rxjs';
import {environment} from '../../../environments/environment';
import {RoleAssignmentDto, RoleAssignmentRequest, RoleAssignmentUserLookupDto} from '../../../models/generated-models';

@Injectable({providedIn: 'root'})
export class RoleAssignApiService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = `${environment.serverUrl}/api/role-assignments`;

  /** Return every active recipient that matches the supplied tax id. */
  lookupUser(taxId: string): Observable<RoleAssignmentUserLookupDto[]> {
    return this.http.get<RoleAssignmentUserLookupDto[]>(`${this.baseUrl}/lookup-user`, {
      params: new HttpParams().set('taxId', taxId),
    });
  }

  searchUsers(query: string): Observable<RoleAssignmentUserLookupDto[]> {
    return this.http.get<RoleAssignmentUserLookupDto[]>(`${this.baseUrl}/search-users`, {
      params: new HttpParams().set('query', query),
    });
  }

  listGranted(search = '', status = ''): Observable<RoleAssignmentDto[]> {
    let params = new HttpParams();
    if (search) {
      params = params.set('search', search);
    }
    if (status) {
      params = params.set('status', status);
    }
    return this.http.get<RoleAssignmentDto[]>(this.baseUrl, {params});
  }

  listReceived(search = '', status = ''): Observable<RoleAssignmentDto[]> {
    let params = new HttpParams();
    if (search) {
      params = params.set('search', search);
    }
    if (status) {
      params = params.set('status', status);
    }
    return this.http.get<RoleAssignmentDto[]>(`${this.baseUrl}/received`, {params});
  }

  get(id: number): Observable<RoleAssignmentDto> {
    return this.http.get<RoleAssignmentDto>(`${this.baseUrl}/${id}`);
  }

  getPendingCount(): Observable<{ count: number }> {
    return this.http.get<{ count: number }>(`${this.baseUrl}/pending-count`);
  }

  /** Currently selected "act on behalf of" account, persisted per user. Null = own account. */
  getActiveAssignment(): Observable<RoleAssignmentDto | null> {
    return this.http.get<RoleAssignmentDto | null>(`${this.baseUrl}/active`);
  }

  /** Persist the switcher selection. Null clears back to the own account. */
  setActiveAssignment(assignmentId: number | null): Observable<RoleAssignmentDto | null> {
    return this.http.put<RoleAssignmentDto | null>(`${this.baseUrl}/active`, {assignmentId});
  }

  accept(id: number): Observable<RoleAssignmentDto> {
    return this.http.post<RoleAssignmentDto>(`${this.baseUrl}/${id}/accept`, {});
  }

  reject(id: number): Observable<RoleAssignmentDto> {
    return this.http.post<RoleAssignmentDto>(`${this.baseUrl}/${id}/reject`, {});
  }

  create(request: RoleAssignmentRequest): Observable<RoleAssignmentDto> {
    return this.http.post<RoleAssignmentDto>(this.baseUrl, request);
  }

  update(id: number, request: RoleAssignmentRequest): Observable<RoleAssignmentDto> {
    return this.http.put<RoleAssignmentDto>(`${this.baseUrl}/${id}`, request);
  }

  revoke(id: number): Observable<RoleAssignmentDto> {
    return this.http.post<RoleAssignmentDto>(`${this.baseUrl}/${id}/revoke`, {});
  }

  delete(id: number): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/${id}`);
  }
}
